import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MainContainer from "../components/MainContainer";
import { priceFormatter } from "../utils/utils";
import { FRONT_URL } from "../utils/constants";
import { clearCart, selectCart } from "../store/slices/cart/slice";
import { createOrder } from "../store/slices/orders/asyncThunks";
import { selectOrder } from "../store/slices/orders/slice";
import { checkCoupon } from "../store/slices/coupons/asyncThunks";
import {
  resetAppliedCoupon,
  selectCoupons,
} from "../store/slices/coupons/slice";
import bundlesService from "../api/services/bundles";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  address: "",
  city: "",
  zipCode: "",
  phone: "",
  email: "",
  details: "",
};

const stepVariants = {
  enter: (dir) => ({ x: dir * 60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir * -60, opacity: 0 }),
};

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);
  const { loading } = useSelector(selectOrder);
  const { applied, checkCouponLoading } = useSelector(selectCoupons);

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [bundles, setBundles] = useState([]);

  const orderDoneRef = useRef(false);

  useEffect(() => {
    if (cart.data.length === 0 && !loading && !orderDoneRef.current) {
      navigate("/prodavnica");
    }
  }, [cart.data.length, loading, navigate]);

  useEffect(() => {
    bundlesService
      .getActiveBundles()
      .then((res) => setBundles(res.data || []))
      .catch(() => setBundles([]));
  }, []);

  // --- Cene ---
  const shippingPrice = 600;

  const total = cart.data.reduce((acc, item) => {
    const priceToUse =
      item.salePrice !== undefined &&
      item.salePrice !== null &&
      item.salePrice !== ""
        ? Number(item.salePrice)
        : Number(item.price);

    return acc + priceToUse * item.quantity;
  }, 0);

  const cartIds = new Set(cart.data.map((item) => item.id));
  const bundleDiscount = bundles
    .filter((b) => cartIds.has(b.productAId) && cartIds.has(b.productBId))
    .reduce((sum, b) => sum + Number(b.discountAmount), 0);

  const isCouponActive = !!applied;
  const discountPercent = isCouponActive ? applied.discount : 0;
  const discountAmount = total * (discountPercent / 100);
  const discountedTotal = Math.max(total - discountAmount - bundleDiscount, 0);
  const isFreeShipping = discountedTotal >= 7000;
  const finalTotal = discountedTotal + (isFreeShipping ? 0 : shippingPrice);

  // --- Forma ---
  const setField = (name) => (e) => {
    const value =
      name === "phone" || name === "zipCode"
        ? e.target.value.replace(/\D/g, "")
        : e.target.value;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.firstName.trim()) err.firstName = "Unesi ime";
    if (!form.lastName.trim()) err.lastName = "Unesi prezime";
    if (!form.address.trim()) err.address = "Unesi ulicu i broj";
    if (!form.city.trim()) err.city = "Unesi mesto";
    if (!form.zipCode.trim() || form.zipCode.trim().length < 4)
      err.zipCode = "Unesi ispravan poštanski broj";
    if (!form.phone.trim() || form.phone.trim().length < 6)
      err.phone = "Unesi ispravan broj telefona";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      err.email = "Unesi ispravnu e-mail adresu";
    return err;
  };

  const goToReview = () => {
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length > 0) return;
    setDirection(1);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const backToForm = () => {
    setDirection(-1);
    setStep(1);
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    setCouponError("");
    dispatch(checkCoupon(couponCode.trim().toUpperCase()))
      .unwrap()
      .then(() => setCouponCode(""))
      .catch((err) => setCouponError(err.message || "Kupon nije važeći"));
  };

  const handleCreateOrder = () => {
    const orderData = {
      firstName: form.firstName,
      lastName: form.lastName,
      address: form.address,
      city: form.city,
      zipCode: form.zipCode,
      phone: form.phone,
      email: form.email,
      products: cart.data,
      couponId: applied?.id,
    };
    if (form.details.trim()) orderData.details = form.details.trim();

    dispatch(createOrder(orderData))
      .unwrap()
      .then(() => {
        orderDoneRef.current = true;
        navigate("/uspesna-porudzbina");
        dispatch(clearCart());
        dispatch(resetAppliedCoupon());
      })
      .catch(() => {
        console.log("Greška pri porudžbini");
      });
  };

  return (
    <MainContainer hideLogo>
      <Head>
        <Kicker>改善 Obsessed — checkout</Kicker>
        <Title>Završi porudžbinu</Title>

        <Steps>
          <Step $active={step === 1} $done={step > 1} onClick={backToForm}>
            <i>{step > 1 ? "✓" : "01"}</i> Podaci
          </Step>
          <StepLine $done={step > 1} />
          <Step $active={step === 2}>
            <i>02</i> Pregled
          </Step>
        </Steps>
      </Head>

      <Grid>
        <FormCol>
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            {step === 1 ? (
              <motion.div
                key="step1"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: [0.22, 0.72, 0.18, 1] }}
              >
                <FieldRow>
                  <Field
                    label="Ime"
                    error={errors.firstName}
                    value={form.firstName}
                    onChange={setField("firstName")}
                    placeholder="npr. Marko"
                  />
                  <Field
                    label="Prezime"
                    error={errors.lastName}
                    value={form.lastName}
                    onChange={setField("lastName")}
                    placeholder="npr. Jovanović"
                  />
                </FieldRow>

                <Field
                  label="Ulica i broj"
                  error={errors.address}
                  value={form.address}
                  onChange={setField("address")}
                  placeholder="npr. Bulevar oslobođenja 12"
                />

                <FieldRow>
                  <Field
                    label="Mesto"
                    error={errors.city}
                    value={form.city}
                    onChange={setField("city")}
                    placeholder="npr. Novi Sad"
                  />
                  <Field
                    label="Poštanski broj"
                    error={errors.zipCode}
                    value={form.zipCode}
                    onChange={setField("zipCode")}
                    placeholder="npr. 21000"
                  />
                </FieldRow>

                <FieldRow>
                  <Field
                    label="Telefon"
                    error={errors.phone}
                    value={form.phone}
                    onChange={setField("phone")}
                    placeholder="npr. 0641234567"
                  />
                  <Field
                    label="E-mail"
                    type="email"
                    error={errors.email}
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="npr. marko@gmail.com"
                  />
                </FieldRow>

                <Field
                  label="Napomena (nije obavezna)"
                  textarea
                  value={form.details}
                  onChange={setField("details")}
                  placeholder="npr. pozvati pre isporuke"
                />

                <PrimaryBtn onClick={goToReview}>
                  Nastavi na pregled <i>→</i>
                </PrimaryBtn>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: [0.22, 0.72, 0.18, 1] }}
              >
                <ReviewBox>
                  <ReviewHead>
                    Podaci za dostavu
                    <button onClick={backToForm}>Izmeni</button>
                  </ReviewHead>
                  <ReviewGrid>
                    <div>
                      <em>Ime i prezime</em>
                      {form.firstName} {form.lastName}
                    </div>
                    <div>
                      <em>Adresa</em>
                      {form.address}, {form.zipCode} {form.city}
                    </div>
                    <div>
                      <em>Telefon</em>
                      {form.phone}
                    </div>
                    <div>
                      <em>E-mail</em>
                      {form.email}
                    </div>
                    {form.details.trim() && (
                      <div className="full">
                        <em>Napomena</em>
                        {form.details}
                      </div>
                    )}
                  </ReviewGrid>
                </ReviewBox>

                <CouponRow>
                  <CouponInput
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError("");
                    }}
                    placeholder="Kupon kod"
                  />
                  <CouponBtn
                    onClick={handleApplyCoupon}
                    disabled={checkCouponLoading}
                  >
                    {checkCouponLoading ? "..." : "Aktiviraj"}
                  </CouponBtn>
                </CouponRow>
                {couponError && <ErrorMsg>{couponError}</ErrorMsg>}
                {isCouponActive && (
                  <CouponOk>
                    ✓ Kupon aktiviran — popust {discountPercent}%
                  </CouponOk>
                )}

                <PrimaryBtn onClick={handleCreateOrder} $loading={loading}>
                  {loading ? "Slanje porudžbine…" : "Poruči — plaćanje pouzećem"}
                </PrimaryBtn>
                <SmallNote>
                  Klikom na dugme potvrđuješ porudžbinu. Plaćaš kuriru pri
                  preuzimanju.
                </SmallNote>
              </motion.div>
            )}
          </AnimatePresence>
        </FormCol>

        <Summary>
          <SummaryTitle>
            Tvoja porudžbina <span>買</span>
          </SummaryTitle>

          <Items>
            {cart.data.map((item) => (
              <Item key={`${item.id}-${item.selectedSize}`}>
                <img
                  src={`${FRONT_URL}/thumbnails/${item.thumbnailImage}`}
                  alt={item.name}
                />
                <div>
                  <b>{item.name}</b>
                  <span>
                    {item.selectedSize} × {item.quantity}
                  </span>
                </div>
                <strong>
                  {priceFormatter(
                    (item?.salePrice || item.price) * item.quantity
                  )}{" "}
                  RSD
                </strong>
              </Item>
            ))}
          </Items>

          <SRow>
            <span>Međuzbir</span>
            <b>{priceFormatter(total)} RSD</b>
          </SRow>
          {isCouponActive && (
            <SRow $accent>
              <span>Kupon (-{discountPercent}%)</span>
              <b>-{priceFormatter(discountAmount)} RSD</b>
            </SRow>
          )}
          {bundleDiscount > 0 && (
            <SRow $green>
              <span>Bundle popust</span>
              <b>-{priceFormatter(bundleDiscount)} RSD</b>
            </SRow>
          )}
          <SRow>
            <span>Isporuka</span>
            <b>
              {isFreeShipping ? "Besplatna" : `${priceFormatter(shippingPrice)} RSD`}
            </b>
          </SRow>

          <STotal>
            <span>Ukupno</span>
            <b>{priceFormatter(finalTotal)} RSD</b>
          </STotal>
        </Summary>
      </Grid>
    </MainContainer>
  );
};

const Field = ({ label, error, textarea, ...rest }) => (
  <FieldWrap>
    <FieldLabel>{label}</FieldLabel>
    {textarea ? (
      <TextArea $error={!!error} {...rest} />
    ) : (
      <Input $error={!!error} {...rest} />
    )}
    {error && <ErrorMsg>{error}</ErrorMsg>}
  </FieldWrap>
);

export default CheckoutPage;

const Head = styled.div`
  width: 100%;
  max-width: 116rem;
  padding: 2rem 2rem 0;
`;

const Kicker = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--red, #fe5f55);
  font-family: "Poppins-Medium";
  margin-bottom: 1.2rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(3rem, 4.6vw, 4.6rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text, #f4f2ee);
  margin-bottom: 2.4rem;
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin-bottom: 1rem;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  font-size: 1.2rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-family: "Poppins-SemiBold";
  color: ${(props) =>
    props.$active
      ? "var(--text, #f4f2ee)"
      : props.$done
        ? "#7dd490"
        : "var(--muted2, #5c5a63)"};
  cursor: ${(props) => (props.$done ? "pointer" : "default")};

  i {
    font-style: normal;
    display: grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    font-size: 1.1rem;
    border: 1px solid
      ${(props) =>
        props.$active
          ? "var(--red, #fe5f55)"
          : props.$done
            ? "#7dd490"
            : "var(--line2, rgba(255,255,255,0.14))"};
    background: ${(props) =>
      props.$active ? "var(--red, #fe5f55)" : "transparent"};
    color: ${(props) =>
      props.$active
        ? "var(--bg0, #0a0a0c)"
        : props.$done
          ? "#7dd490"
          : "var(--muted2, #5c5a63)"};
  }
`;

const StepLine = styled.div`
  width: 6rem;
  height: 1px;
  background: ${(props) =>
    props.$done ? "#7dd490" : "var(--line2, rgba(255,255,255,0.14))"};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3.6rem;
  align-items: start;
  width: 100%;
  max-width: 116rem;
  padding: 0 2rem 4rem;

  @media (max-width: 1030px) {
    grid-template-columns: 1fr;
  }
`;

const FormCol = styled.div`
  width: 100%;
  overflow: hidden;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1.4rem;

  & > div {
    flex: 1;
  }

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FieldWrap = styled.div`
  margin-bottom: 1.8rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 1.1rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-family: "Poppins-Medium";
  color: var(--muted, #8e8c94);
  margin-bottom: 0.7rem;
`;

const inputBase = css`
  width: 100%;
  padding: 1.2rem 1.4rem;
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  background: var(--bg1, #101013);
  color: var(--text, #f4f2ee);
  border: 1px solid
    ${(props) =>
      props.$error
        ? "var(--red, #fe5f55)"
        : "var(--line2, rgba(255,255,255,0.14))"};
  border-radius: 0.4rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const Input = styled.input`
  ${inputBase}
  height: 4.6rem;
`;

const TextArea = styled.textarea`
  ${inputBase}
  min-height: 9rem;
  resize: vertical;
`;

const ErrorMsg = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  color: var(--red, #fe5f55);
  margin-top: 0.6rem;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 1.6rem;
  margin-top: 1rem;
  border: none;
  border-radius: 0.4rem;
  background: ${(props) =>
    props.$loading ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$loading ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.4rem;
  cursor: ${(props) => (props.$loading ? "wait" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  transition: background 0.3s var(--ease, ease);

  i {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: ${(props) =>
      props.$loading ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};

    i {
      transform: translateX(0.4rem);
    }
  }
`;

const SmallNote = styled.p`
  font-size: 1.2rem;
  font-family: "Poppins-Regular";
  color: var(--muted2, #5c5a63);
  text-align: center;
  margin-top: 1.2rem;
`;

const ReviewBox = styled.div`
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.4rem;
  background: var(--bg1, #101013);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const ReviewHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.2rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-family: "Poppins-SemiBold";
  color: var(--text, #f4f2ee);
  margin-bottom: 1.6rem;

  button {
    background: none;
    border: none;
    color: var(--red, #fe5f55);
    font-size: 1.1rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-family: "Poppins-Medium";
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.6rem;

  & > div {
    font-size: 1.4rem;
    font-family: "Poppins-Medium";
    color: var(--text, #f4f2ee);

    em {
      display: block;
      font-style: normal;
      font-size: 1.1rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      font-family: "Poppins-Regular";
      color: var(--muted2, #5c5a63);
      margin-bottom: 0.4rem;
    }
  }

  .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const CouponRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.6rem;
`;

const CouponInput = styled.input`
  ${inputBase}
  height: 4.4rem;
  flex: 1;
`;

const CouponBtn = styled.button`
  background: transparent;
  color: var(--red, #fe5f55);
  border: 1px solid rgba(254, 95, 85, 0.5);
  border-radius: 0.4rem;
  padding: 0 1.8rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: var(--red, #fe5f55);
    color: var(--bg0, #0a0a0c);
  }
`;

const CouponOk = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  color: #7dd490;
  margin-bottom: 1rem;
`;

const Summary = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.6rem;
  position: sticky;
  top: 2rem;

  @media (max-width: 1030px) {
    position: static;
    order: -1;
  }
`;

const SummaryTitle = styled.h3`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: 2rem;
  letter-spacing: 0.04em;
  color: var(--text, #f4f2ee);
  margin-bottom: 2rem;

  span {
    font-family: var(--font-kanji);
    font-size: 0.7em;
    color: var(--red, #fe5f55);
    vertical-align: middle;
    margin-left: 0.4rem;
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  margin-bottom: 1.2rem;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 4.8rem 1fr auto;
  gap: 1.2rem;
  align-items: center;
  padding: 1rem 0;

  img {
    width: 4.8rem;
    height: 5.8rem;
    object-fit: cover;
    border-radius: 0.3rem;
    border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;

    b {
      font-size: 1.3rem;
      font-family: "Poppins-SemiBold";
      color: var(--text, #f4f2ee);
    }

    span {
      font-size: 1.2rem;
      font-family: "Poppins-Regular";
      color: var(--muted, #8e8c94);
    }
  }

  strong {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    font-size: 1.4rem;
    color: var(--text, #f4f2ee);
  }
`;

const SRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.7rem 0;
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  color: ${(props) =>
    props.$green
      ? "#7dd490"
      : props.$accent
        ? "var(--red, #fe5f55)"
        : "var(--muted, #8e8c94)"};

  b {
    font-family: "Poppins-SemiBold";
    color: ${(props) =>
      props.$green
        ? "#7dd490"
        : props.$accent
          ? "var(--red, #fe5f55)"
          : "var(--text, #f4f2ee)"};
  }
`;

const STotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-top: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  margin-top: 1.2rem;
  padding-top: 1.6rem;
  font-size: 1.5rem;
  font-family: "Poppins-Medium";
  color: var(--text, #f4f2ee);

  b {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    font-size: 2.6rem;
    letter-spacing: 0.02em;
    color: var(--red, #fe5f55);
  }
`;
